import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  // Start non-blocking: trust the cached session synchronously. We only need
  // `loading=true` for the genuinely-unknown cold case (no cached session at all),
  // so returning users never see a full-page auth spinner on navigation.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let authSubscription;

    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (sessionError) {
          console.error('Error getting session:', sessionError);
        } else if (currentSession) {
          // Cached session exists — populate immediately and stop loading.
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes (token refresh, sign-in, sign-out).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!isMounted) return;
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);

        if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
        }
      }
    );
    authSubscription = subscription;

    return () => {
      isMounted = false;
      authSubscription?.unsubscribe();
    };
  }, []);

  // Sign up with email and password
  const signUp = async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      // Normalize the email so case/whitespace variations match existing users.
      const normalizedEmail = (email || '').trim().toLowerCase();

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
      });

      if (signUpError) throw signUpError;

      // Supabase deliberately hides whether an email already exists (to prevent
      // user enumeration). When it does, signUp returns 200 with a user object
      // but an EMPTY identities array (no new identity was created). That empty
      // array is the only signal that the email is already registered.
      if (data?.user && (!data.identities || data.identities.length === 0)) {
        const existsError = {
          code: 'email_exists',
          message: 'An account with this email already exists.',
        };
        setError(existsError.message);
        return { data: null, error: existsError };
      }

      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    setError(null);
    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;
      setUser(null);
      setSession(null);
      return { error: null };
    } catch (err) {
      setError(err.message);
      return { error: err };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    setError(null);
    try {
      const { data, error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) throw resetError;

      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    setError(null);
    try {
      const { data, error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    }
  };

  // Sign in with Google OAuth - matching sensorpushweb implementation
  const signInWithGoogle = async () => {
    setError(null);
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      console.log('🔐 Initiating Google OAuth sign-in');
      console.log('🔗 Google OAuth redirect URL:', redirectUrl);

      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account', // Allow returning users to select account
          },
          skipBrowserRedirect: false, // Let browser handle redirect
        },
      });

      if (oauthError) {
        console.error('❌ Google OAuth error:', oauthError);
        throw oauthError;
      }

      console.log('✅ Google OAuth initiated successfully');
      console.log('📊 OAuth data:', data);
      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    }
  };

  // Delete account via Supabase Edge Function (same as React Native app)
  const deleteAccount = async () => {
    if (!session?.access_token) {
      return { error: { message: 'No active session' } };
    }

    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        return { error: { message: 'Missing Supabase configuration' } };
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/delete-user-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return { error: { message: result.error || 'Account deletion failed' } };
      }

      // Sign out to clear session
      try {
        await supabase.auth.signOut();
      } catch (e) {
        // Ignore sign-out errors post-deletion
      }

      setUser(null);
      setSession(null);

      return { error: null };
    } catch (err) {
      return { error: { message: err.message || 'Account deletion failed' } };
    }
  };

  // Sign in with Apple using native Apple Sign In JS SDK
  // Uses Services ID for web, exchanges identity token with Supabase via signInWithIdToken
  const signInWithApple = async () => {
    setError(null);
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const clientId = 'com.mazinoamuno.sensorpushapp.apple';

      console.log('🍎 Initiating Apple Sign In JS');
      console.log('🔗 Client ID (Services ID):', clientId);
      console.log('🔗 Redirect URI:', redirectUrl);

      // Configure Apple Sign In
      window.AppleID.auth.init({
        clientId: clientId,
        scope: 'name email',
        redirectURI: redirectUrl,
        state: crypto.randomUUID(),
        usePopup: true,
      });

      // Trigger Apple Sign In popup
      const response = await window.AppleID.auth.signIn();
      console.log('🍎 Apple Sign In response received');

      if (!response.authorization || !response.authorization.id_token) {
        throw new Error('No identity token received from Apple');
      }

      const idToken = response.authorization.id_token;
      console.log('🔑 Identity token received, exchanging with Supabase...');

      // Exchange the Apple identity token with Supabase
      const { data, error: tokenError } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: idToken,
      });

      if (tokenError) {
        console.error('❌ Supabase signInWithIdToken error:', tokenError);
        throw tokenError;
      }

      console.log('✅ Apple sign-in successful via ID token!');
      console.log('� User:', data.session?.user?.email);
      return { data, error: null };
    } catch (err) {
      console.error('❌ Apple sign-in failed:', err);
      // Apple SDK throws { error: 'popup_closed_by_user' } when user cancels
      if (err.error === 'popup_closed_by_user' || err.error === 'AUTH_CANCELED') {
        return { data: null, error: { message: 'Sign in cancelled' } };
      }
      setError(err.message || err.error || 'Apple sign-in failed');
      return { data: null, error: err };
    }
  };

  const value = {
    user,
    session,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    signInWithGoogle,
    signInWithApple,
    deleteAccount,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
